import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import useDebouncedSearch from "hooks/useDebouncedSearch";
import Button from "@mui/material/Button";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@outpave/ds";
import CompanyFilterType from "../../enums/CompanyFilterType";
import AccountsTable from "../../components/platform/accounts/AccountsTable";
import NewAccountDrawer from "components/platform/accounts/NewAccountDrawer";
import { useDrawerRecord } from "hooks/useDrawerRecord";

export type CompanyFilterCriteriaProps = {
  companyFilterType?: string;
  searchTerm?: string;
};

/** What a link into this page can hand it, such as a dashboard row naming its company. */
export interface AccountsLocationState {
  search?: string;
}

const Accounts: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useLocation();
  const initialSearch = (state as AccountsLocationState | null)?.search ?? "";
  const [activeTab, setActiveTab] = useState(0);
  const [searchTerm, setSearchTerm] = useState(initialSearch);
  const search = useDebouncedSearch(setSearchTerm, initialSearch);

  const [filterCriteria, setFilterCriteria] =
    useState<CompanyFilterCriteriaProps>({
      companyFilterType: CompanyFilterType.ShowAll,
      searchTerm: initialSearch,
    });

  const tabConfigurations = [
    {
      label: t("Show All"),
      companyFilterType: CompanyFilterType.ShowAll,
    },
    {
      label: t("Active"),
      companyFilterType: CompanyFilterType.Active,
    },
    {
      label: t("Declined"),
      companyFilterType: CompanyFilterType.Declined,
    },
  ];

  useEffect(() => {
    setFilterCriteria({
      companyFilterType: tabConfigurations[activeTab].companyFilterType,
      searchTerm: searchTerm,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, searchTerm]);

  const [refreshAccounts, setRefreshAccounts] = useState(0);

  const handleRefreshAccounts = () => {
    setRefreshAccounts((prev) => prev + 1);
  };

  // A fresh form per open: the drawer mounts on show and unmounts once it has slid out.
  const newAccountDrawer = useDrawerRecord<true>();

  const handleCloseNewAccount = (refresh: boolean = false) => {
    refresh && handleRefreshAccounts();
    newAccountDrawer.close();
  };

  return (
    <PageContainer
      title={t("platform.manageAccounts")}
      primaryAction={
        <Button onClick={() => newAccountDrawer.show(true)}>
          {t("platform.addAccount")}
        </Button>
      }
      tabs={{
        items: tabConfigurations.map((tab, index) => ({
          id: String(index),
          label: t(tab.label),
        })),
        activeId: String(activeTab),
        onChange: (id) => setActiveTab(Number(id)),
      }}
      search={{ ...search, placeholder: t("Search accounts") }}
    >
      <AccountsTable
        filterCriteria={filterCriteria}
        refreshAccounts={refreshAccounts}
      />

      {newAccountDrawer.record && (
        <NewAccountDrawer
          open={newAccountDrawer.open}
          handleClose={handleCloseNewAccount}
          onAfterClose={newAccountDrawer.afterClose}
        />
      )}
    </PageContainer>
  );
};

export default Accounts;
